/**
 * PLC Dashboard — fully standalone page for Priority Life Care.
 *
 * Architecturally separate from the MorningStar DashboardPage because the
 * underlying data model is fundamentally different (WelcomeHome vs Aline,
 * two-axis portfolios vs named portfolios, community IDs vs unique
 * strings). Sharing the same page component meant cross-cutting gates
 * (`!isPLC && ...`) which made the routing intent murky. This component
 * owns its own header + main and is routed directly from main.tsx.
 *
 * Includes:
 *   - Top bar (Lupa brand + "back to Lupa" + client identity)
 *   - Two-axis portfolio filter (Ownership Group × Community Group)
 *   - View tabs: Weekly / Monthly / YoY
 *   - Current-state cards, period-comparison cards, trend charts,
 *     community breakdown table
 *
 * Backend dispatches to plc_dashboard.* when client_id=plc.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import BrandIcon from '../assets/DWA_WolfHead_Favicon_Web.png';
import { LeadSourcesPanel } from './LeadSourcesPanel';
import { useAuth } from '../contexts/AuthContext';
import { authService, authFetch } from '../services/auth';
import {
  fetchWeekly,
  fetchMonthly,
  fetchTrends,
  downloadWeeklyPdf,
  downloadWeeklyCsv,
  downloadMonthlyPdf,
  downloadMonthlyCsv,
  type DashboardWeekly,
  type DashboardMonthly,
  type DashboardTrends,
} from '../services/dashboardApi';

// ─── Theme (PLC client palette, baked in) ──────────────────────────────────

const PLC_THEME = {
  pageBg: '#f0f7f7',
  cardBg: '#ffffff',
  cardBorder: 'rgba(0,0,0,0.08)',
  cardShadow: '0 1px 4px rgba(0,0,0,0.06)',
  accent: '#0D9488',          // Darkened teal
  highlight: '#B8960C',       // Dark sunflower
  sectionTitle: '#4C4894',    // Dark lavender
  sectionDivider: 'rgba(76,72,148,0.2)',
  textPrimary: '#1a202c',
  textSecondary: '#4a5568',
  textMuted: '#94a3b8',
  rowDivider: 'rgba(0,0,0,0.06)',
  positive: '#15803d',
  negative: '#dc2626',
};

const TOPBAR: React.CSSProperties = {
  background: 'rgba(10,10,10,0.97)',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
};

// ─── Types ──────────────────────────────────────────────────────────────────

export interface PLCDashboardTheme {
  pageBg: string;
  cardBg: string;
  cardBorder: string;
  cardShadow: string;
  accent: string;
  highlight?: string;
  sectionTitle: string;
  sectionDivider: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  rowDivider: string;
  positive: string;
  negative: string;
}

interface PortfolioAxis {
  key: string;
  label: string;
  community_count: number;
}

interface PortfolioResponse {
  client_id: 'plc';
  axes: {
    ownership_groups: PortfolioAxis[];
    community_groups: PortfolioAxis[];
  };
  total_communities: number;
}

interface PLCCommunityRow {
  community_id: number;
  name: string;
  ownership_group: string;
  community_group: string;
  domain: string | null;
  total_units: number;
  occupied_units: number;
  vacant_units: number;
  occupancy_rate: number;
}

type ReportView = 'weekly' | 'monthly' | 'yoy';

// ─── Formatters ─────────────────────────────────────────────────────────────

function fmt(n: number | null | undefined): string {
  return n != null ? n.toLocaleString() : '—';
}

function pct(n: number | null | undefined, signed = false): string {
  if (n == null) return '—';
  const v = (n * 100).toFixed(1);
  return signed && n > 0 ? `+${v}%` : `${v}%`;
}

function fmtWeekTick(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

// ─── Sub-components ─────────────────────────────────────────────────────────

const MetricCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  subTone?: 'neutral' | 'positive' | 'negative';
  theme: PLCDashboardTheme;
}> = ({ label, value, sub, subTone = 'neutral', theme }) => {
  const subColor =
    subTone === 'positive' ? theme.positive :
    subTone === 'negative' ? theme.negative :
    theme.textMuted;
  return (
    <div style={{
      background: theme.cardBg,
      border: `1px solid ${theme.cardBorder}`,
      boxShadow: theme.cardShadow,
      borderRadius: 4,
      padding: '1.25rem 1.5rem',
    }}>
      <p style={{
        fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
        textTransform: 'uppercase', color: theme.textSecondary, marginBottom: '0.5rem',
      }}>{label}</p>
      <p style={{
        fontSize: '1.7rem', fontWeight: 700, color: theme.textPrimary, lineHeight: 1,
      }}>{value}</p>
      {sub && (
        <p style={{ fontSize: '0.78rem', color: subColor, marginTop: '0.4rem', fontWeight: 500 }}>
          {sub}
        </p>
      )}
    </div>
  );
};

const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  theme: PLCDashboardTheme;
}> = ({ title, subtitle, children, theme }) => (
  <div style={{
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    boxShadow: theme.cardShadow,
    borderRadius: 4,
    padding: '1rem 1.25rem 0.6rem',
  }}>
    <h3 style={{
      fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
      textTransform: 'uppercase', color: theme.sectionTitle,
      marginBottom: subtitle ? '0.15rem' : '0.75rem', paddingBottom: '0.5rem',
      borderBottom: `1px solid ${theme.sectionDivider}`,
    }}>{title}</h3>
    {subtitle && (
      <p style={{ fontSize: '0.7rem', color: theme.textMuted, marginBottom: '0.5rem' }}>
        {subtitle}
      </p>
    )}
    {children}
  </div>
);

// ─── Main page ──────────────────────────────────────────────────────────────

export const PLCDashboardPage: React.FC = () => {
  const theme = PLC_THEME;
  const { logout } = useAuth();
  // ── Portfolio filter state (two-axis) ─────────────────────────────────
  const [portfolios, setPortfolios] = useState<PortfolioResponse | null>(null);
  const [ownershipGroup, setOwnershipGroup] = useState<string>('');
  const [communityGroup, setCommunityGroup] = useState<string>('');
  const [communityId, setCommunityId] = useState<number | null>(null);

  // ── View tab state ─────────────────────────────────────────────────────
  const [reportView, setReportView] = useState<ReportView>('weekly');

  // ── Data state ─────────────────────────────────────────────────────────
  const [weeklyData, setWeeklyData] = useState<DashboardWeekly | null>(null);
  const [monthlyData, setMonthlyData] = useState<DashboardMonthly | null>(null);
  const [trendsData, setTrendsData] = useState<DashboardTrends | null>(null);
  const [communitiesData, setCommunitiesData] = useState<PLCCommunityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingKind, setDownloadingKind] = useState<'pdf' | 'csv' | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = useCallback(async (kind: 'pdf' | 'csv') => {
    setDownloadingKind(kind);
    setDownloadError(null);
    try {
      const isWeekly = reportView === 'weekly';
      const compare = reportView === 'yoy' ? 'yoy' : 'mom';
      const baseOpts = {
        ownershipGroup: ownershipGroup || undefined,
        communityGroup: communityGroup || undefined,
        communityId: communityId ?? undefined,
      };
      if (kind === 'pdf') {
        if (isWeekly) {
          await downloadWeeklyPdf('plc', baseOpts);
        } else {
          await downloadMonthlyPdf('plc', { ...baseOpts, compare });
        }
      } else {
        if (isWeekly) {
          await downloadWeeklyCsv('plc', baseOpts);
        } else {
          await downloadMonthlyCsv('plc', { ...baseOpts, compare });
        }
      }
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloadingKind(null);
    }
  }, [reportView, ownershipGroup, communityGroup, communityId]);

  // ── Common filter params we send to the backend ────────────────────────
  const filterParams = useMemo(() => ({
    ownershipGroup: ownershipGroup || undefined,
    communityGroup: communityGroup || undefined,
    communityId: communityId ?? undefined,
  }), [ownershipGroup, communityGroup, communityId]);

  // ── Load portfolios once ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    authFetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/dashboard/plc/portfolios`, {
      headers: { Authorization: `Bearer ${authService.getAccessToken() ?? ''}` },
    })
      .then(r => r.json())
      .then((p: PortfolioResponse) => { if (!cancelled) setPortfolios(p); })
      .catch(() => { /* fallback to empty axes */ });
    return () => { cancelled = true; };
  }, []);

  // ── Load report data when filter or view changes ──────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Weekly + monthly use the same /weekly and /monthly endpoints with
      // PLC-aware backend dispatch.
      const compare = reportView === 'yoy' ? 'yoy' : 'mom';
      const [wRes, mRes, tRes] = await Promise.all([
        fetchWeekly('plc', filterParams),
        fetchMonthly('plc', { ...filterParams, compare: compare as 'mom' | 'yoy' }),
        fetchTrends('plc', { weeks: 8, ...filterParams }),
      ]);
      setWeeklyData(wRes);
      setMonthlyData(mRes);
      setTrendsData(tRes);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [filterParams, reportView]);

  useEffect(() => { load(); }, [load]);

  // ── Community breakdown (separate fetch — refreshes on filter change) ─
  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    if (ownershipGroup) params.set('ownershipGroup', ownershipGroup);
    if (communityGroup) params.set('communityGroup', communityGroup);
    const qs = params.toString();
    const url = `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/dashboard/plc/communities${qs ? `?${qs}` : ''}`;
    authFetch(url, {
      headers: { Authorization: `Bearer ${authService.getAccessToken() ?? ''}` },
    })
      .then(r => r.json())
      .then((rows: PLCCommunityRow[]) => { if (!cancelled) setCommunitiesData(rows); })
      .catch(() => { /* empty list */ });
    return () => { cancelled = true; };
  }, [ownershipGroup, communityGroup]);

  // ── Resolve "current" data based on view ──────────────────────────────
  const isYoY = reportView === 'yoy';
  const isWeekly = reportView === 'weekly';
  const activeReportData = isWeekly ? weeklyData : monthlyData;
  const totals = (activeReportData?.totals) as Record<string, number | null> | undefined;
  const currentState = activeReportData?.current_state;
  const periodLabel = isWeekly ? 'Weekly' : isYoY ? 'YoY' : 'Monthly';
  const currentReadable = isWeekly ? 'This Week' : 'This Month';
  const priorReadable = isWeekly ? 'Prior Week' : isYoY ? 'Same Month Last Year' : 'Prior Month';
  const changeShort = isWeekly ? 'WoW' : isYoY ? 'YoY' : 'MoM';
  const curSuffix = isWeekly ? 'tw' : 'tm';
  const priSuffix = isWeekly ? 'pw' : 'pm';
  const changeKey = isWeekly ? 'wow' : 'mom';
  const leadsCur = (totals?.[`leads_${curSuffix}`] ?? 0) as number;
  const leadsPri = (totals?.[`leads_${priSuffix}`] ?? 0) as number;
  const leadsChange = (totals?.[`leads_${changeKey}_pct`] ?? null) as number | null;
  const toursCur = (totals?.[`tours_${curSuffix}`] ?? 0) as number;
  const toursPri = (totals?.[`tours_${priSuffix}`] ?? 0) as number;
  const toursChange = (totals?.[`tours_${changeKey}_pct`] ?? null) as number | null;

  // ── Trends chart series ────────────────────────────────────────────────
  const weekPoints = useMemo(() => {
    if (!trendsData) return [];
    return trendsData.weeks.map(w => ({
      week_label: fmtWeekTick(w.week_start),
      leads: w.leads,
      tours: w.tours,
    }));
  }, [trendsData]);
  const occupancyPoints = useMemo(() => {
    if (!trendsData) return [];
    return trendsData.occupancy.map(o => ({
      date_label: fmtWeekTick(o.snapshot_date),
      occupancy_pct: Math.round(o.occupancy_rate * 1000) / 10,
    }));
  }, [trendsData]);

  // ── Community drilldown options ────────────────────────────────────────
  const scopedCommunities = useMemo(() => communitiesData, [communitiesData]);

  // ── Community breakdown rows (from weekly/monthly response) ────────────
  // The weekly + monthly endpoints both return a `communities` array with
  // per-community leads / tours / FU stats. We merge in ownership + group
  // (already on the row from the backend) so the breakdown table can render
  // the same shape as MorningStar.
  const breakdownRows = useMemo(() => {
    const raw = (activeReportData?.communities ?? []) as unknown as Array<Record<string, unknown>>;
    return raw.map(c => ({
      community_id: c.community_id as number,
      community_name: (c.community_name as string | null) ?? `Community ${c.community_id}`,
      ownership_group: (c.ownership_group as string | null) ?? '—',
      community_group: (c.community_group as string | null) ?? '—',
      leads_cur: (c[`leads_${curSuffix}`] as number | null) ?? 0,
      leads_pri: (c[`leads_${priSuffix}`] as number | null) ?? 0,
      leads_change_pct: (c[`leads_${changeKey}_pct`] as number | null) ?? null,
      leads_change_delta: (c[`leads_${changeKey}_delta`] as number | null) ?? 0,
      tours_cur: (c[`tours_${curSuffix}`] as number | null) ?? 0,
      tours_pri: (c[`tours_${priSuffix}`] as number | null) ?? 0,
      tours_change_pct: (c[`tours_${changeKey}_pct`] as number | null) ?? null,
      tours_change_delta: (c[`tours_${changeKey}_delta`] as number | null) ?? 0,
      fu_rate_cur: (c[`fu_rate_${curSuffix}`] as number | null) ?? null,
      fu_rate_pri: (c[`fu_rate_${priSuffix}`] as number | null) ?? null,
      fu_rate_delta_pp: (c.fu_rate_delta_pp as number | null) ?? null,
    }));
  }, [activeReportData, curSuffix, priSuffix, changeKey]);

  return (
    <div style={{ minHeight: '100vh', background: theme.pageBg, fontFamily: 'Lato, system-ui, sans-serif' }}>
      {/* ─── Top bar ──────────────────────────────────────────────────── */}
      <header style={{
        ...TOPBAR,
        padding: '0.875rem 1.5rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'linear-gradient(135deg, #1a1a1a, #2d2d2d)',
            border: `1px solid rgba(254,195,92,0.3)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <img src={BrandIcon} alt="Lupa" style={{ width: 22, height: 22, objectFit: 'cover' }} />
          </div>

          {/* DWA users navigate via the shared Hub header (hubs/HubHeader.tsx). */}

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{
              display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
              background: theme.accent, flexShrink: 0,
            }} />
            <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#e2e8f0' }}>Priority Life Care</span>
            <span style={{
              fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: theme.sectionTitle, background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
              padding: '0.1rem 0.45rem', borderRadius: '3px',
            }}>
              Dashboard
            </span>
          </div>
        </div>

        {/* Sign out — client-role users land here with no sidebar / user
            menu, so this is their only way to log out. */}
        <button
          onClick={() => { logout(); }}
          style={{
            fontSize: '0.75rem', padding: '0.3rem 0.8rem', borderRadius: '4px', fontFamily: 'inherit',
            cursor: 'pointer', background: 'none',
            border: '1px solid rgba(255,255,255,0.14)', color: '#a0aec0', fontWeight: 500,
          }}
          onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#a0aec0'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.14)'; }}
        >
          Sign Out
        </button>
      </header>

      <main style={{ padding: '1.5rem', maxWidth: '1440px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* ─── Two-axis portfolio selector ─────────────────────────────── */}
      <div style={{
        background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
        boxShadow: theme.cardShadow, borderRadius: 4, padding: '0.85rem 1rem',
        display: 'flex', flexWrap: 'wrap', gap: '1.25rem', alignItems: 'center',
      }}>
        <div>
          <label style={{
            fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
            textTransform: 'uppercase', color: theme.textSecondary,
            marginRight: '0.6rem',
          }}>Ownership</label>
          <select
            value={ownershipGroup}
            onChange={e => setOwnershipGroup(e.target.value)}
            style={{
              fontSize: '0.85rem', padding: '0.35rem 0.55rem', borderRadius: 4,
              border: `1px solid ${theme.cardBorder}`, background: theme.cardBg,
              color: theme.textPrimary, fontFamily: 'inherit', cursor: 'pointer',
              minWidth: 180,
            }}
          >
            <option value="">All Ownerships</option>
            {(portfolios?.axes.ownership_groups ?? []).map(o => (
              <option key={o.key} value={o.key}>{o.label} ({o.community_count})</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{
            fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
            textTransform: 'uppercase', color: theme.textSecondary,
            marginRight: '0.6rem',
          }}>Community Group</label>
          <select
            value={communityGroup}
            onChange={e => setCommunityGroup(e.target.value)}
            style={{
              fontSize: '0.85rem', padding: '0.35rem 0.55rem', borderRadius: 4,
              border: `1px solid ${theme.cardBorder}`, background: theme.cardBg,
              color: theme.textPrimary, fontFamily: 'inherit', cursor: 'pointer',
              minWidth: 180,
            }}
          >
            <option value="">All Groups</option>
            {(portfolios?.axes.community_groups ?? []).map(g => (
              <option key={g.key} value={g.key}>{g.label} ({g.community_count})</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{
            fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
            textTransform: 'uppercase', color: theme.textSecondary,
            marginRight: '0.6rem',
          }}>Community</label>
          <select
            value={communityId ?? ''}
            onChange={e => setCommunityId(e.target.value ? parseInt(e.target.value, 10) : null)}
            style={{
              fontSize: '0.85rem', padding: '0.35rem 0.55rem', borderRadius: 4,
              border: `1px solid ${theme.cardBorder}`, background: theme.cardBg,
              color: theme.textPrimary, fontFamily: 'inherit', cursor: 'pointer',
              minWidth: 240,
            }}
          >
            <option value="">All Communities ({scopedCommunities.length || portfolios?.total_communities || 0})</option>
            {scopedCommunities.map(c => (
              <option key={c.community_id} value={c.community_id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ─── View tab strip + download buttons ──────────────────────── */}
      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <span style={{
            fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
            textTransform: 'uppercase', color: theme.textSecondary,
            marginRight: '0.5rem',
          }}>Report</span>
          {(['weekly', 'monthly', 'yoy'] as ReportView[]).map(view => {
            const active = reportView === view;
            const label = view === 'weekly' ? 'Weekly' : view === 'monthly' ? 'Monthly' : 'YoY';
            return (
              <button
                key={view}
                onClick={() => setReportView(view)}
                style={{
                  fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.04em',
                  padding: '0.4rem 0.95rem', borderRadius: 3, cursor: 'pointer',
                  fontFamily: 'inherit',
                  background: active ? theme.sectionTitle : 'transparent',
                  color: active ? '#ffffff' : theme.textSecondary,
                  border: `1px solid ${active ? theme.sectionTitle : theme.cardBorder}`,
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => handleDownload('pdf')}
            disabled={downloadingKind !== null || !activeReportData}
            style={{
              fontSize: '0.78rem', fontWeight: 600,
              padding: '0.4rem 0.95rem', borderRadius: 4,
              cursor: downloadingKind !== null || !activeReportData ? 'default' : 'pointer',
              fontFamily: 'inherit',
              background: theme.accent, color: '#ffffff',
              border: `1px solid ${theme.accent}`,
              opacity: downloadingKind !== null || !activeReportData ? 0.6 : 1, whiteSpace: 'nowrap',
            }}
          >
            {downloadingKind === 'pdf' ? 'Generating…' : '⬇ PDF'}
          </button>
          <button
            type="button"
            onClick={() => handleDownload('csv')}
            disabled={downloadingKind !== null || !activeReportData}
            style={{
              fontSize: '0.78rem', fontWeight: 600,
              padding: '0.4rem 0.95rem', borderRadius: 4,
              cursor: downloadingKind !== null || !activeReportData ? 'default' : 'pointer',
              fontFamily: 'inherit',
              background: 'transparent', color: theme.textPrimary,
              border: `1px solid ${theme.cardBorder}`,
              opacity: downloadingKind !== null || !activeReportData ? 0.6 : 1, whiteSpace: 'nowrap',
            }}
          >
            {downloadingKind === 'csv' ? 'Generating…' : '⬇ CSV'}
          </button>
        </div>
      </div>
      {downloadError && (
        <p style={{ color: theme.negative, fontSize: '0.78rem', margin: 0 }}>{downloadError}</p>
      )}

      {loading && !activeReportData && (
        <p style={{ color: theme.textMuted, padding: '2rem' }}>Loading PLC dashboard…</p>
      )}
      {error && (
        <p style={{ color: theme.negative, padding: '1rem 1.5rem' }}>Error: {error}</p>
      )}

      {activeReportData && (
        <>
          {/* ─── Header ──────────────────────────────────────────────── */}
          <div style={{
            background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
            boxShadow: theme.cardShadow, borderRadius: 4, padding: '1rem 1.5rem',
          }}>
            <p style={{
              fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase',
              color: theme.textSecondary, fontWeight: 600, marginBottom: '0.35rem',
            }}>Priority Life Care — {periodLabel} Operator Report</p>
            <p style={{ fontSize: '0.85rem', color: theme.textPrimary, margin: 0 }}>
              {currentReadable} vs {priorReadable}
            </p>
            {activeReportData.snapshot_at && (
              <p style={{ fontSize: '0.7rem', color: theme.textMuted, marginTop: '0.25rem' }}>
                Snapshot: {new Date(activeReportData.snapshot_at).toLocaleString()}
              </p>
            )}
          </div>

          {/* ─── Current State cards ─────────────────────────────────── */}
          {currentState && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
              <MetricCard
                label="Occupancy Rate"
                value={pct(currentState.occupancy_rate)}
                sub={`${fmt(currentState.occupied_units)} / ${fmt(currentState.total_units)} units`}
                theme={theme}
              />
              <MetricCard
                label="Active Leads"
                value={fmt(currentState.active_leads)}
                sub="active prospects"
                theme={theme}
              />
              <MetricCard
                label={`Move-ins ${currentReadable}`}
                value={fmt(currentState.move_ins_in_window)}
                sub="gross"
                theme={theme}
              />
              <MetricCard
                label={`Move-outs ${currentReadable}`}
                value={fmt(currentState.move_outs_in_window)}
                sub="gross"
                theme={theme}
              />
              <MetricCard
                label={`Net Change ${currentReadable}`}
                value={currentState.net_move_change > 0
                  ? `+${currentState.net_move_change}` : fmt(currentState.net_move_change)}
                sub="move-ins − move-outs"
                subTone={
                  currentState.net_move_change > 0 ? 'positive'
                  : currentState.net_move_change < 0 ? 'negative' : 'neutral'
                }
                theme={theme}
              />
            </div>
          )}

          {/* ─── Period comparison cards ─────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            <MetricCard
              label={`Leads ${currentReadable}`}
              value={fmt(leadsCur)}
              sub={leadsChange != null ? `${pct(leadsChange, true)} ${changeShort}` : 'no prior data'}
              subTone={leadsChange == null ? 'neutral' : leadsChange >= 0 ? 'positive' : 'negative'}
              theme={theme}
            />
            <MetricCard
              label={`Leads ${priorReadable}`}
              value={fmt(leadsPri)}
              sub="reference"
              theme={theme}
            />
            <MetricCard
              label={`Tours ${currentReadable}`}
              value={fmt(toursCur)}
              sub={toursChange != null ? `${pct(toursChange, true)} ${changeShort}` : 'no prior data'}
              subTone={toursChange == null ? 'neutral' : toursChange >= 0 ? 'positive' : 'negative'}
              theme={theme}
            />
            <MetricCard
              label={`Tours ${priorReadable}`}
              value={fmt(toursPri)}
              sub="reference"
              theme={theme}
            />
          </div>

          {/* ─── Trend charts ────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '1rem' }}>
            <ChartCard title="Leads & Tours — Weekly" subtitle="Last 8 weeks." theme={theme}>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={weekPoints} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
                  <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="week_label" stroke={theme.textMuted} fontSize={11} />
                  <YAxis stroke={theme.textMuted} fontSize={11} />
                  <Tooltip />
                  <Line type="monotone" dataKey="leads" stroke={theme.accent} strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="tours" stroke={theme.highlight || theme.accent} strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Occupancy — Daily" subtitle="As of snapshot." theme={theme}>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={occupancyPoints} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
                  <CartesianGrid stroke={theme.sectionDivider} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date_label" stroke={theme.textMuted} fontSize={11} />
                  <YAxis stroke={theme.textMuted} fontSize={11} domain={['auto', 'auto']} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v) => `${(v as number).toFixed(1)}%`} />
                  <Line type="monotone" dataKey="occupancy_pct" stroke={theme.accent} strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* ─── Lead source breakdown ────────────────────────────────── */}
          <LeadSourcesPanel
            clientId="plc"
            theme={{
              cardBg: theme.cardBg,
              cardBorder: theme.cardBorder,
              cardShadow: theme.cardShadow,
              accent: theme.accent,
              sectionTitle: theme.sectionTitle,
              sectionDivider: theme.sectionDivider,
              textPrimary: theme.textPrimary,
              textSecondary: theme.textSecondary,
              textMuted: theme.textMuted,
              rowDivider: theme.sectionDivider,
              positive: theme.positive,
              negative: theme.negative,
            }}
            period={isWeekly ? 'week' : 'month'}
            ownershipGroup={ownershipGroup || undefined}
            communityGroup={communityGroup || undefined}
            communityId={communityId ?? undefined}
            compare={isYoY ? 'yoy' : 'previous'}
            limit={10}
          />

          {/* ─── Community breakdown table ───────────────────────────── */}
          <div style={{
            background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
            boxShadow: theme.cardShadow, borderRadius: 4, padding: '1.25rem 1.5rem',
          }}>
            <h3 style={{
              fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
              textTransform: 'uppercase', color: theme.sectionTitle,
              marginBottom: '1rem', paddingBottom: '0.5rem',
              borderBottom: `1px solid ${theme.sectionDivider}`,
            }}>Community Breakdown ({breakdownRows.length})</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${theme.sectionDivider}` }}>
                    <th style={th(theme, 'left')}>Community</th>
                    <th style={th(theme, 'left')}>Ownership</th>
                    <th style={th(theme, 'left')}>Group</th>
                    <th style={th(theme)}>Leads {curSuffix.toUpperCase()}</th>
                    <th style={th(theme)}>Leads {priSuffix.toUpperCase()}</th>
                    <th style={th(theme)}>Leads {changeShort}</th>
                    <th style={th(theme)}>Tours {curSuffix.toUpperCase()}</th>
                    <th style={th(theme)}>Tours {priSuffix.toUpperCase()}</th>
                    <th style={th(theme)}>Tours {changeShort}</th>
                    <th style={th(theme)}>FU Rate {curSuffix.toUpperCase()}</th>
                    <th style={th(theme)}>FU Rate {priSuffix.toUpperCase()}</th>
                    <th style={th(theme)}>FU Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdownRows.map(c => {
                    const leadsTone =
                      c.leads_change_delta > 0 ? theme.positive :
                      c.leads_change_delta < 0 ? theme.negative : theme.textMuted;
                    const toursTone =
                      c.tours_change_delta > 0 ? theme.positive :
                      c.tours_change_delta < 0 ? theme.negative : theme.textMuted;
                    const fuDeltaTone =
                      c.fu_rate_delta_pp == null ? theme.textMuted :
                      c.fu_rate_delta_pp > 0 ? theme.negative :
                      c.fu_rate_delta_pp < 0 ? theme.positive : theme.textMuted;
                    return (
                      <tr
                        key={c.community_id}
                        style={{
                          borderBottom: `1px solid ${theme.rowDivider}`,
                          cursor: 'pointer',
                          background: communityId === c.community_id ? `${theme.accent}10` : 'transparent',
                        }}
                        onClick={() => setCommunityId(communityId === c.community_id ? null : c.community_id)}
                      >
                        <td style={td(theme, 'left')}>{c.community_name}</td>
                        <td style={td(theme, 'left')}>{c.ownership_group}</td>
                        <td style={td(theme, 'left')}>{c.community_group}</td>
                        <td style={td(theme)}>{fmt(c.leads_cur)}</td>
                        <td style={td(theme)}>{fmt(c.leads_pri)}</td>
                        <td style={{ ...td(theme), color: leadsTone, fontWeight: 600 }}>
                          {c.leads_change_pct == null ? '—' : pct(c.leads_change_pct, true)}
                        </td>
                        <td style={td(theme)}>{fmt(c.tours_cur)}</td>
                        <td style={td(theme)}>{fmt(c.tours_pri)}</td>
                        <td style={{ ...td(theme), color: toursTone, fontWeight: 600 }}>
                          {c.tours_change_pct == null ? '—' : pct(c.tours_change_pct, true)}
                        </td>
                        <td style={td(theme)}>{pct(c.fu_rate_cur)}</td>
                        <td style={td(theme)}>{pct(c.fu_rate_pri)}</td>
                        <td style={{ ...td(theme), color: fuDeltaTone, fontWeight: 600 }}>
                          {c.fu_rate_delta_pp == null ? '—' : pct(c.fu_rate_delta_pp, true)}
                        </td>
                      </tr>
                    );
                  })}
                  {breakdownRows.length === 0 && (
                    <tr>
                      <td style={td(theme, 'left')} colSpan={12}>
                        <span style={{ color: theme.textMuted }}>No communities match the current filter.</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {breakdownRows.length > 0 && (
              <p style={{ fontSize: '0.72rem', color: theme.textMuted, marginTop: '0.6rem' }}>
                Click a row to drill the rest of the dashboard down to that community. Click again to clear.
              </p>
            )}
          </div>
        </>
      )}
      </main>
    </div>
  );
};

// ─── Table style helpers ────────────────────────────────────────────────────

function th(theme: PLCDashboardTheme, align: 'left' | 'right' = 'right'): React.CSSProperties {
  return {
    padding: '0.5rem 0.5rem',
    textAlign: align,
    fontSize: '0.7rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.textSecondary,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
}

function td(theme: PLCDashboardTheme, align: 'left' | 'right' = 'right'): React.CSSProperties {
  return {
    padding: '0.45rem 0.5rem',
    textAlign: align,
    color: theme.textPrimary,
    whiteSpace: 'nowrap',
  };
}

export default PLCDashboardPage;
